"use client";

import Link from "next/link";
import Script from "next/script";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { getSectionRoute } from "../../lib/navigationRoutes";
import {
  bodyTextClass,
  mutedBodyTextClass,
  sectionHeadingClass,
  sectionIntroClass,
  sectionLabelClass
} from "../typography";

const scheduleMeetingUrl = "https://calendly.com/merukimoon/30min";
const turnstileErrorMessage = "Please complete the verification and try again.";

type HeadingLevel = "h1" | "h2";

type ContactSectionProps = {
  headingLevel?: HeadingLevel;
};

function LinkedInIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
      fill="currentColor"
    >
      <path d="M20.447 20.452H16.89v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.345V9h3.414v1.561h.049c.476-.9 1.637-1.85 3.37-1.85 3.602 0 4.268 2.37 4.268 5.455v6.286zM5.337 7.433a1.987 1.987 0 1 1 0-3.974 1.987 1.987 0 0 1 0 3.974zM7.119 20.452H3.555V9h3.564v11.452z" />
    </svg>
  );
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          size?: "invisible" | "compact" | "normal";
        }
      ) => string;
      execute: (widgetId?: string, options?: { async?: boolean }) => Promise<string> | void;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export default function ContactSection({ headingLevel = "h2" }: ContactSectionProps): JSX.Element {
  const { t, locale } = useLanguage();
  const HeadingTag: HeadingLevel = headingLevel;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState(""); // Honeypot field for spam detection
  const [loading, setLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const turnstilePendingRef = useRef<{
    resolve: (token: string) => void;
    reject: (error: Error) => void;
  } | null>(null);
  const turnstileTimeoutRef = useRef<number | null>(null);

  const embeddedSiteKey = useMemo(() => process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? null, []);

  useEffect(() => {
    if (embeddedSiteKey) {
      setTurnstileSiteKey(embeddedSiteKey);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/turnstile-sitekey", { method: "GET" });
        if (!res.ok) return;
        const json = (await res.json()) as { siteKey?: string };
        if (cancelled) return;
        if (typeof json.siteKey === "string" && json.siteKey.trim().length > 0) {
          setTurnstileSiteKey(json.siteKey.trim());
        }
      } catch {
        // Ignore, we will simply not render Turnstile.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [embeddedSiteKey]);

  useEffect(() => {
    if (!turnstileReady) return;
    if (!turnstileSiteKey) return;
    if (!turnstileContainerRef.current) return;
    if (!window.turnstile) return;
    if (turnstileWidgetIdRef.current) return;

    const widgetId = window.turnstile.render(turnstileContainerRef.current, {
      sitekey: turnstileSiteKey,
      size: "invisible",
      callback: (token) => {
        if (turnstilePendingRef.current) {
          turnstilePendingRef.current.resolve(token);
          turnstilePendingRef.current = null;
        }
        if (turnstileTimeoutRef.current !== null) {
          window.clearTimeout(turnstileTimeoutRef.current);
          turnstileTimeoutRef.current = null;
        }
      },
      "expired-callback": () => {
        if (turnstilePendingRef.current) {
          turnstilePendingRef.current.reject(new Error(turnstileErrorMessage));
          turnstilePendingRef.current = null;
        }
        if (turnstileTimeoutRef.current !== null) {
          window.clearTimeout(turnstileTimeoutRef.current);
          turnstileTimeoutRef.current = null;
        }
      },
      "error-callback": () => {
        if (turnstilePendingRef.current) {
          turnstilePendingRef.current.reject(new Error(turnstileErrorMessage));
          turnstilePendingRef.current = null;
        }
        if (turnstileTimeoutRef.current !== null) {
          window.clearTimeout(turnstileTimeoutRef.current);
          turnstileTimeoutRef.current = null;
        }
      },
    });
    turnstileWidgetIdRef.current = widgetId;

    return () => {
      try {
        if (turnstileWidgetIdRef.current) {
          window.turnstile?.remove(turnstileWidgetIdRef.current);
        }
      } finally {
        turnstileWidgetIdRef.current = null;
        turnstilePendingRef.current = null;
        if (turnstileTimeoutRef.current !== null) {
          window.clearTimeout(turnstileTimeoutRef.current);
          turnstileTimeoutRef.current = null;
        }
      }
    };
  }, [turnstileReady, turnstileSiteKey]);

  useEffect(() => {
    if (!isSent) return;
    const turnstile = window.turnstile;
    const widgetId = turnstileWidgetIdRef.current;
    if (turnstile && widgetId) {
      try {
        turnstile.remove(widgetId);
      } catch {
        // Ignore cleanup errors.
      }
    }
    turnstileWidgetIdRef.current = null;
    turnstilePendingRef.current = null;
    if (turnstileTimeoutRef.current !== null) {
      window.clearTimeout(turnstileTimeoutRef.current);
      turnstileTimeoutRef.current = null;
    }
  }, [isSent]);

  const requestTurnstileToken = async (): Promise<string> => {
    if (!turnstileSiteKey) {
      throw new Error(turnstileErrorMessage);
    }
    const turnstile = window.turnstile;
    const widgetId = turnstileWidgetIdRef.current;
    if (!turnstileReady || !turnstile || !widgetId) {
      throw new Error(turnstileErrorMessage);
    }

    return new Promise((resolve, reject) => {
      if (turnstilePendingRef.current) {
        reject(new Error(turnstileErrorMessage));
        return;
      }

      let settled = false;
      const settle = (action: () => void): void => {
        if (settled) return;
        settled = true;
        if (turnstileTimeoutRef.current !== null) {
          window.clearTimeout(turnstileTimeoutRef.current);
          turnstileTimeoutRef.current = null;
        }
        turnstilePendingRef.current = null;
        action();
      };

      const resolveToken = (token: string): void => {
        if (!token) {
          settle(() => reject(new Error(turnstileErrorMessage)));
          return;
        }
        settle(() => resolve(token));
      };

      const rejectToken = (error: Error): void => {
        settle(() => reject(error));
      };

      turnstilePendingRef.current = {
        resolve: resolveToken,
        reject: rejectToken,
      };

      turnstileTimeoutRef.current = window.setTimeout(() => {
        rejectToken(new Error(turnstileErrorMessage));
      }, 10000);

      try {
        turnstile.reset(widgetId);
        const execResult = turnstile.execute(widgetId, { async: true });
        if (execResult && typeof (execResult as Promise<string>).then === "function") {
          (execResult as Promise<string>)
            .then((token) => resolveToken(token))
            .catch((err) =>
              rejectToken(err instanceof Error ? err : new Error(turnstileErrorMessage))
            );
        }
      } catch (err) {
        rejectToken(err instanceof Error ? err : new Error(turnstileErrorMessage));
      }
    });
  };

  const resetTurnstile = (): void => {
    const turnstile = window.turnstile;
    const widgetId = turnstileWidgetIdRef.current;
    if (widgetId && turnstile) {
      turnstile.reset(widgetId);
    }
    turnstilePendingRef.current = null;
    if (turnstileTimeoutRef.current !== null) {
      window.clearTimeout(turnstileTimeoutRef.current);
      turnstileTimeoutRef.current = null;
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setIsVerifying(true);
    setError(null);
    if (honeypot.trim().length > 0) {
      setIsVerifying(false);
      setLoading(false);
      setIsSent(true);
      return;
    }
    let turnstileToken: string;
    try {
      turnstileToken = await requestTurnstileToken();
    } catch (err) {
      setIsVerifying(false);
      setLoading(false);
      setError(err instanceof Error ? err.message : turnstileErrorMessage);
      return;
    }
    setIsVerifying(false);
    // Only submit allowed fields (company is optional)
    const payload = {
      name: name.trim(),
      email: email.trim(),
      company: company.trim() || undefined,
      message: message.trim(),
      website: honeypot.trim() || undefined,
      turnstileToken,
    };
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let responseBody: unknown = null;
      try {
        responseBody = await res.json();
      } catch {
        responseBody = null;
      }
      const apiResponse = responseBody as { ok?: boolean; error?: string } | null;

      if (res.ok && apiResponse?.ok) {
        setName("");
        setEmail("");
        setCompany("");
        setMessage("");
        setHoneypot("");
        setIsSent(true);
      } else {
        setError(apiResponse?.error ?? "We couldn't send your message. Please try again or contact us by email.");
      }
    } catch {
      setError("Network error. Please check your internet connection and try again.");
    } finally {
      resetTurnstile();
      setLoading(false);
    }
  };

  const handleReset = (): void => {
    resetTurnstile();
    setIsSent(false);
    setError(null);
    setName("");
    setEmail("");
    setCompany("");
    setMessage("");
    setIsVerifying(false);
  };

  const contactActionLinkClassName =
    "inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 no-underline hover:text-slate-900 dark:hover:text-slate-100 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900";

  return (
    <section id="contact" className="scroll-mt-28 py-16" data-testid="contact-section">
      <div className="mx-auto max-w-5xl px-4">
        <div className="flex flex-col gap-12 md:grid md:grid-cols-2 md:items-start">
          {/* Left column */}
          <div>
            <p className={sectionLabelClass}>{t.common.labels.contact}</p>
            <HeadingTag className={`${sectionHeadingClass} rtl:text-right`}>{t.contact.title}</HeadingTag>
            <p className={`mt-4 ${sectionIntroClass} leading-relaxed whitespace-pre-line`}>{t.contact.intro}</p>
            <p className={`mt-3 ${mutedBodyTextClass} leading-relaxed`}>
              {t.contact.decisionSupportHint}{" "}
              <Link
                href={getSectionRoute(locale, "decisions")}
                className="font-semibold text-sky-700 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
              >
                {t.contact.decisionSupportHintLinkLabel}
              </Link>
            </p>
            <div className="mt-6">
              <a
                href={scheduleMeetingUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-primary-600 px-4 py-2 text-white shadow-sm transition-colors hover:bg-primary-700"
              >
                {t.hero.ctaCard.button}
              </a>
            </div>
            <div className="mt-4 flex flex-col gap-1 text-sm font-medium text-slate-600 dark:text-slate-300">
              <a
                href={`mailto:${t.common.footer.email}`}
                aria-label={t.common.footer.contactAriaLabel}
                className={contactActionLinkClassName}
              >
                <EnvelopeIcon className="h-5 w-5" aria-hidden="true" />
                <span>{t.common.footer.contactAriaLabel}</span>
              </a>
              <a
                href="https://www.linkedin.com/in/merukimoon"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t.common.footer.linkedinAriaLabel}
                className={contactActionLinkClassName}
              >
                <LinkedInIcon className="h-5 w-5" />
                <span>{t.common.footer.linkedinAriaLabel}</span>
              </a>
            </div>
            <div className={`mt-6 rounded-2xl bg-slate-100 px-4 py-3 dark:bg-slate-800 ${bodyTextClass}`}>
              {t.contact.availability}
            </div>
          </div>

          {/* Right column: form / success */}
          <div
            className="rounded-2xl border border-slate-200 bg-white/80 p-6 shadow-sm backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/80"
          >
            {!isSent ? (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <Script
                  src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
                  async
                  defer
                  onLoad={() => setTurnstileReady(true)}
                />
                {/* Honeypot field - hidden from users, visible to bots */}
                <div className="sr-only" aria-hidden="true">
                  <label htmlFor="website">
                    Website (leave empty)
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      style={{ position: "absolute", left: "-9999px" }}
                    />
                  </label>
                </div>
                <label className="block space-y-1 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {t.contact.fields.name}
                  <input
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 px-4 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 dark:focus:border-sky-400"
                    placeholder={t.contact.fields.placeholders.name}
                    type="text"
                    data-testid="contact-name"
                  />
                </label>
                <label className="block space-y-1 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {t.contact.fields.email}
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 px-4 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 dark:focus:border-sky-400"
                    placeholder={t.contact.fields.placeholders.email}
                    data-testid="contact-email"
                  />
                </label>
                <label className="block space-y-1 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {t.contact.fields.company}
                  <input
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 px-4 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 dark:focus:border-sky-400"
                    placeholder={t.contact.fields.placeholders.company}
                    type="text"
                    data-testid="contact-company"
                  />
                </label>
                <label className="block space-y-1 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {t.contact.fields.message}
                  <textarea
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="min-h-[120px] w-full rounded-xl border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 px-4 py-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 dark:focus:border-sky-400"
                    placeholder={t.contact.fields.placeholders.message}
                    data-testid="contact-message"
                  />
                </label>
                {error && <p className="text-sm font-semibold text-red-600">{error}</p>}
                {turnstileSiteKey && (
                  <div className="h-0 w-0 overflow-hidden" aria-hidden="true">
                    <div ref={turnstileContainerRef} />
                  </div>
                )}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-start">
                  <button
                    type="submit"
                    disabled={loading || isVerifying}
                    className="w-full sm:w-auto rounded-xl bg-sky-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-70"
                    data-testid="contact-submit"
                  >
                    {loading ? "Sending…" : t.common.buttons.submit}
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex min-h-[420px] flex-col items-center justify-center text-center space-y-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-100">
                  ✓
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                    {t.contact.thanks}
                  </h3>
                  <p className={`max-w-md ${bodyTextClass}`}>
                    {t.contact.availability}
                  </p>
                  <div className="flex flex-col items-center gap-1 text-sm font-medium text-slate-600 dark:text-slate-300">
                    <a
                      href={`mailto:${t.common.footer.email}`}
                      aria-label={t.common.footer.contactAriaLabel}
                      className={contactActionLinkClassName}
                    >
                      <EnvelopeIcon className="h-5 w-5" aria-hidden="true" />
                      <span>{t.common.footer.contactAriaLabel}</span>
                    </a>
                    <a
                      href="https://www.linkedin.com/in/merukimoon"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t.common.footer.linkedinAriaLabel}
                      className={contactActionLinkClassName}
                    >
                      <LinkedInIcon className="h-5 w-5" />
                      <span>{t.common.footer.linkedinAriaLabel}</span>
                    </a>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleReset}
                  className="mt-2 rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-800 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
                >
                  {t.contact.sendAnotherMessage}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
